import { DurationString, isDurationString } from '@chronosphereio/core';
import { QueryParamConfig } from 'use-query-params';
import { isValidDate } from '@/core';

export type TimeOptionValue = Date | DurationString | null | undefined;

/* Interprets an encoded string and returns either the string or null/undefined if not available */
function getEncodedValue(
  input: string | Array<string | null> | null | undefined,
  allowEmptyString?: boolean
): string | null | undefined {
  if (input == null) {
    return input;
  }
  // '' or []
  if (input.length === 0 && (!allowEmptyString || (allowEmptyString && input !== ''))) {
    return null;
  }

  const str = input instanceof Array ? input[0] : input;
  if (str == null) {
    return str;
  }
  if (!allowEmptyString && str === '') {
    return null;
  }

  return str;
}

/* Encodes individual TimeRangeValue as a string, depends on whether start is relative or absolute */
function encodeTimeRangeValue(timeOptionValue: TimeOptionValue): string | null | undefined {
  if (!timeOptionValue) {
    return timeOptionValue;
  }

  if (typeof timeOptionValue === 'string') {
    if (isDurationString(timeOptionValue)) {
      return timeOptionValue;
    }
  }
  return timeOptionValue.getTime().toString();
}

// Check for a JS timestamp, which should be 13 digits.
const TIMESTAMP_REGEX = /\d{13}/;

/* Converts param input to supported relative or absolute time range format */
function decodeTimeRangeValue(
  input: string | Array<string | null> | null | undefined
): Date | DurationString | null | undefined {
  const paramString = getEncodedValue(input);
  if (paramString == null) return paramString;

  if (isDurationString(paramString)) {
    return paramString;
  }

  if (typeof paramString === 'string') {
    // Attempt to parse out timestamps because we have some edge cases in alerts where
    // the query string is erroneously appended with some extra content.
    const timestamp = paramString.match(TIMESTAMP_REGEX);
    if (!timestamp) {
      return undefined;
    }

    const timestampDate = new Date(Number(timestamp[0]));
    // Verify we have a real date.
    if (isValidDate(timestampDate)) {
      return timestampDate;
    }
  }

  return undefined;
}

/**
 * Custom TimeRangeValue param type
 * See: https://github.com/pbeshai/use-query-params/tree/master/packages/serialize-query-params#param-types
 */
export const TimeRangeParam: QueryParamConfig<TimeOptionValue, TimeOptionValue> = {
  encode: encodeTimeRangeValue,
  decode: decodeTimeRangeValue,
  equals: (valueA: TimeOptionValue, valueB: TimeOptionValue) => {
    if (valueA === valueB) return true;
    if (valueA == null || valueB == null) return valueA === valueB;
    return valueA.valueOf() === valueB.valueOf();
  },
};
